import type { MisconceptionDefinition } from '../types';

/**
 * TIJD — Eenheid-omrekenen sub-registry.
 *
 * Misconcepties rond conversies tussen tijdseenheden: uren ↔ minuten ↔
 * seconden, gemengde notaties (2u30min → minuten), en decimale uren
 * (1,5 u → 90 min). Veel van deze fouten komen door verwarring met het
 * decimaal stelsel (×60 vs ×100, ÷60 vs ÷100).
 *
 * Families: TIJD-OMREKENEN, TIJD-SAMENGESTELD (voor gemengde-tijd
 * conversies die ook in samengestelde context terugkomen).
 *
 * Onderdeel van de TIJD-barrel-splitsing (juni 2026).
 */
export const TIJD_OMREKENEN_MISCONCEPTIONS: MisconceptionDefinition[] = [
// ─── Uren → minuten ─────────────────────────────────────────────────────
{
  id: 'tijd.uur-min-keer-10',
  label: 'Uren × 10 i.p.v. × 60',
  module: 'Tijd',
  description: 'Leerling vermenigvuldigt uren met 10 in plaats van met 60 bij omrekenen naar minuten.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.uur-min-keer-100',
  label: 'Uren × 100 i.p.v. × 60',
  module: 'Tijd',
  description: 'Leerling vermenigvuldigt uren met 100 in plaats van met 60 (verwarring met decimaal stelsel).',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.uur-min-dubbeltellen',
  label: 'Uren dubbel geteld',
  module: 'Tijd',
  description: 'Leerling vermenigvuldigt het resultaat van uren × 60 nogmaals met 2 of telt het dubbel.',
  typicalFamilies: ['TIJD-OMREKENEN']
},
// ─── Minuten → uren ─────────────────────────────────────────────────────
{
  id: 'tijd.min-uur-deel-120',
  label: 'Minuten ÷ 120 i.p.v. ÷ 60',
  module: 'Tijd',
  description: 'Leerling deelt minuten door 120 in plaats van door 60 bij omrekenen naar uren.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.min-uur-cijfers-omdraaien',
  label: 'Cijfers omgedraaid bij min→uur',
  module: 'Tijd',
  description: 'Leerling pakt de eerste cijfers van het minuten-getal als antwoord (bijv. 240 min → 24 uur).',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.min-uur-deel-6',
  label: 'Minuten ÷ 6 i.p.v. ÷ 60',
  module: 'Tijd',
  description: 'Leerling deelt minuten door 6 in plaats van door 60 (een nul vergeten).',
  typicalFamilies: ['TIJD-OMREKENEN']
},
// ─── Seconden → minuten ─────────────────────────────────────────────────
{
  id: 'tijd.sec-min-deel-6',
  label: 'Seconden ÷ 6 i.p.v. ÷ 60',
  module: 'Tijd',
  description: 'Leerling deelt seconden door 6 in plaats van door 60 bij omrekenen naar minuten.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.sec-min-deel-100',
  label: 'Seconden ÷ 100 i.p.v. ÷ 60',
  module: 'Tijd',
  description: 'Leerling deelt seconden door 100 in plaats van door 60 (verwarring met decimaal stelsel).',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.sec-min-deel-10',
  label: 'Seconden ÷ 10 i.p.v. ÷ 60',
  module: 'Tijd',
  description: 'Leerling deelt seconden door 10 in plaats van door 60 bij omrekenen naar minuten.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.sec-min-rekenfout-4',
  label: 'Deelfout sec→min (4 i.p.v. 7)',
  module: 'Tijd',
  description: 'Leerling maakt een deelfout bij seconden ÷ 60 en komt op een te laag aantal minuten uit.',
  typicalFamilies: ['TIJD-OMREKENEN']
},
// ─── Gemengd uur+min → minuten ──────────────────────────────────────────
{
  id: 'tijd.gemengd-cijfers-plakken',
  label: 'Cijfers geplakt bij gemengde tijd',
  module: 'Tijd',
  description: 'Leerling plakt de uren en losse minuten aan elkaar (bijv. 2u30min → 230 minuten) in plaats van te rekenen.',
  typicalFamilies: ['TIJD-OMREKENEN', 'TIJD-SAMENGESTELD']
}, {
  id: 'tijd.gemengd-1-uur-vergeten',
  label: 'Eén uur vergeten in gemengde tijd',
  module: 'Tijd',
  description: 'Leerling rekent maar één uur i.p.v. het volledige aantal uren bij omrekenen van gemengde tijd naar minuten.',
  typicalFamilies: ['TIJD-OMREKENEN', 'TIJD-SAMENGESTELD']
}, {
  id: 'tijd.gemengd-keer-100',
  label: 'Gemengde tijd × 100 i.p.v. × 60',
  module: 'Tijd',
  description: 'Leerling vermenigvuldigt de uren met 100 in plaats van met 60 bij omrekenen van gemengde tijd.',
  typicalFamilies: ['TIJD-OMREKENEN', 'TIJD-SAMENGESTELD']
},
// ─── Gemengd min+sec → seconden ─────────────────────────────────────────
{
  id: 'tijd.gemengd-sec-plakken',
  label: 'Cijfers geplakt (min+sec → sec)',
  module: 'Tijd',
  description: 'Leerling plakt de minuten en losse seconden aan elkaar i.p.v. te rekenen (bijv. 3min20sec → 320 seconden).',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.gemengd-sec-keer-20',
  label: 'Minuten × 20 i.p.v. × 60',
  module: 'Tijd',
  description: 'Leerling vermenigvuldigt minuten met het getal seconden uit de vraag i.p.v. met 60.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.gemengd-sec-2-min',
  label: 'Verkeerd aantal minuten gerekend',
  module: 'Tijd',
  description: 'Leerling rekent met één minuut minder dan in de vraag staat bij min+sec → seconden.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.gemengd-sec-plakken-245',
  label: 'Cijfers geplakt (2+45=245)',
  module: 'Tijd',
  description: 'Leerling plakt minuten en seconden aan elkaar bij omrekenen naar seconden (variant van cijfers-plakken).',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.gemengd-sec-1-min',
  label: 'Eén minuut gerekend i.p.v. twee',
  module: 'Tijd',
  description: 'Leerling neemt slechts 1 minuut mee in plaats van het werkelijke aantal minuten uit de vraag.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.gemengd-sec-4-min',
  label: 'Vier minuten gerekend i.p.v. twee',
  module: 'Tijd',
  description: 'Leerling rekent met meer minuten dan in de vraag staat (vaak het dubbele) bij min+sec → seconden.',
  typicalFamilies: ['TIJD-OMREKENEN']
},
// ─── Minuten → uren+min (omgekeerd, met rest) ───────────────────────────
{
  id: 'tijd.min-gemengd-1-uur',
  label: 'Maar 1×60 afgetrokken',
  module: 'Tijd',
  description: 'Leerling trekt slechts 1 × 60 af bij minuten → uren+min, waardoor de rest groter dan 60 blijft.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.min-gemengd-splitsen',
  label: 'Cijfers gesplitst bij min→uur+min',
  module: 'Tijd',
  description: 'Leerling splitst het minuten-getal op cijferniveau (bijv. 195 → 19 uur 5 min) i.p.v. te delen door 60.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.min-gemengd-rest-boven-60',
  label: 'Rest boven 60 laten staan',
  module: 'Tijd',
  description: 'Leerling laat het rest-aantal minuten boven 60 staan (bijv. 2u75min) i.p.v. door te rekenen.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.min-gemengd-deel-100',
  label: 'Minuten ÷ 100 i.p.v. ÷ 60',
  module: 'Tijd',
  description: 'Leerling deelt minuten door 100 in plaats van door 60 bij omrekenen naar uren+minuten.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.min-gemengd-splitsen-500',
  label: 'Grote cijfers gesplitst (500→50u0min)',
  module: 'Tijd',
  description: 'Leerling splitst grote minuten-getallen op cijferniveau (bijv. 500 → 50 uur 0 min).',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.min-gemengd-rest-fout',
  label: 'Rest verkeerd berekend',
  module: 'Tijd',
  description: 'Leerling berekent het aantal hele uren correct maar de rest-minuten verkeerd.',
  typicalFamilies: ['TIJD-OMREKENEN']
},
// ─── Decimale uren ↔ minuten ────────────────────────────────────────────
{
  id: 'tijd.decimaal-2-5-uur',
  label: '1,5 uur verward met 2,5 uur',
  module: 'Tijd',
  description: 'Leerling rekent 2,5 × 60 in plaats van 1,5 × 60 (verkeerd getal aan komma).',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.decimaal-komma-weg',
  label: 'Komma genegeerd (1,5 → 15)',
  module: 'Tijd',
  description: 'Leerling laat de komma weg en behandelt 1,5 als 15 bij omrekenen naar minuten.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.decimaal-0-5-als-45',
  label: '0,5 uur als 45 minuten gerekend',
  module: 'Tijd',
  description: 'Leerling denkt dat 0,5 uur gelijk is aan 45 minuten in plaats van 30 minuten.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.min-dec-uur-fout',
  label: 'Deelfout bij min → decimale uren',
  module: 'Tijd',
  description: 'Leerling deelt door 60 maar maakt een rekenfout en komt op de helft van het juiste antwoord uit.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.min-dec-uur-deel-10',
  label: 'Min → dec uur ÷ 10 i.p.v. ÷ 60',
  module: 'Tijd',
  description: 'Leerling deelt minuten door 10 in plaats van door 60 bij omrekenen naar decimale uren.',
  typicalFamilies: ['TIJD-OMREKENEN']
}, {
  id: 'tijd.min-dec-uur-deel-6',
  label: 'Min → dec uur ÷ 6 i.p.v. ÷ 60',
  module: 'Tijd',
  description: 'Leerling deelt minuten door 6 in plaats van door 60 bij omrekenen naar decimale uren.',
  typicalFamilies: ['TIJD-OMREKENEN']
}];